import { Alert, Form, Space, Typography, Upload } from 'antd'
import CIMSFormItem from '../../../../../../../../../../components/CIMSFormItem'
import CIMSSelect from '../../../../../../../../../../components/CIMSSelect'
import CIMSSection from '../../../../../../../../../../components/CIMSSection'
import CIMSTable from '../../../../../../../../../../components/CIMSTable'
import { CheckCircleTwoTone, InboxOutlined } from '@ant-design/icons'
import { useEffect, useState } from 'react'
import { parse } from 'csv-parse/browser/esm/sync'
import { CIMSModal } from '../../../../../../../../../../components/CIMSModal'
import { useGlobalState } from '../../../../../../../../../../context/GlobalStateContext'
import { errorHandler } from '../../../../../../../../../../hooks/errorHandler'
import { fetchJSON } from '../../../../../../../../../../utils/fetchJSON'
import { useForm } from 'antd/lib/form/Form'
import { CSVEntity } from 'shared'
import { RcFile } from 'antd/lib/upload'

interface ImportModalProps {
  onCancel: () => void
  onOk: () => void
  visible: boolean
}

interface ImportModalForm {
  entityId: string
  upsertKey?: string | null
  file?: RcFile | null
}

/**
 * Initial form values
 */
const INITIAL_VALUES = {
  entityId: CSVEntity.PayItemNoGeometry.id,
}

function ImportModal({ onOk, onCancel, ...rest }: ImportModalProps) {
  const {
    state: { projectGlobalId: projectId },
  } = useGlobalState()
  const [form] = useForm()
  const { Dragger } = Upload
  const entityId = Form.useWatch('entityId', form) ?? INITIAL_VALUES.entityId
  // Re-render table rows when upsertKey changes
  Form.useWatch('upsertKey', form)

  const [columns, setColumns] = useState<string[]>([])
  const rows = columns.map((column) => {
    return {
      column,
      isValid: CSVEntity.PayItemNoGeometry.importColumns.includes(column),
      isUpsertKey: column === form.getFieldValue('upsertKey'),
    }
  })
  const isValid = rows.some(({ isValid }) => {
    return isValid
  })
  const [loading, setLoading] = useState(false)
  const [csvError, setCSVError] = useState<string>()
  const allowedColumns = CSVEntity.fromId(entityId)?.importColumns ?? []

  useEffect(() => {
    form.setFieldValue('upsertKey', undefined)
  }, [form, entityId])

  function reset() {
    form.resetFields()
    setColumns([])
    setLoading(false)
    setCSVError(undefined)
  }

  return (
    <CIMSModal
      width="default"
      title="Import CSV"
      initialFormValues={
        {
          ...INITIAL_VALUES,
          file: null,
        } as ImportModalForm
      }
      loading={loading}
      externalForm={form}
      onCancel={() => {
        reset()
        onCancel()
      }}
      onOk={async ({ file, entityId, upsertKey }) => {
        if (!file) {
          return errorHandler('File must be present')
        }

        if (!projectId) {
          return errorHandler('Project id must be defined')
        }

        const formData = new FormData()
        formData.append('file', file)
        // Validated so using bang
        formData.append('entityId', entityId!)
        // Validated so using bang
        formData.append('upsertKey', upsertKey!)

        setLoading(true)
        const { error } = await fetchJSON({
          url: `/api/projects/${projectId.toUpperCase()}/csv/imports`,
          requestInit: {
            method: 'POST',
            body: formData,
          },
        })
        setLoading(false)

        if (error) {
          errorHandler(error)
          return
        }

        reset()
        onOk()
      }}
      disabled={!isValid}
      {...rest}
    >
      <Space
        direction="vertical"
        size="middle"
        style={{ maxWidth: '700px', marginBottom: '32px' }}
      >
        <Typography.Text>Select a record type to import.</Typography.Text>
        <Typography.Text>
          Records will be created or updated based on the column you choose as
          the <strong>"Upsert key"</strong>.
        </Typography.Text>
        <Typography.Text>
          If a row matches an existing record using the Upsert key, that record
          will be updated. If there's no match, a new record will be created.
        </Typography.Text>
        <Typography.Text>
          New records will automatically get a system-generated global ID.
        </Typography.Text>
        <Typography.Text>
          If a cell in your CSV is blank, it will clear the current value for
          that field — except for the Upsert key, which is required to find the
          record and won’t be cleared.
        </Typography.Text>

        <Space size="small" direction="vertical">
          <Typography.Text>
            Column headers are case-sensitive and must match an expected field
            name in order to be imported:
          </Typography.Text>
          <Typography.Text strong>
            {[...allowedColumns].join(', ')}
          </Typography.Text>
        </Space>
      </Space>

      <div style={{ maxWidth: '400px' }}>
        <CIMSFormItem
          label="Record type"
          name="entityId"
          help={
            'Select a record type to determine the kind of data being imported. This ensures the system processes the CSV file correctly.'
          }
          requiredMessage="Please select a record type"
        >
          <CIMSSelect
            simple
            style={{ minWidth: 250 }}
            options={CSVEntity.filter((item) => !!item.importColumns.length)
              .sort((a, b) =>
                a.classTypeName
                  .toLowerCase()
                  .localeCompare(b.classTypeName.toLowerCase())
              )
              .map((entity) => {
                return {
                  label: entity.classType,
                  value: entity.id,
                }
              })}
          />
        </CIMSFormItem>
        <CIMSFormItem
          label="Upsert key"
          name="upsertKey"
          help={
            'The upsert key is used to identify rows in the CSV that should update existing records. Rows without a matching upsert key will create new records.'
          }
          requiredMessage="Please select an upsert key"
        >
          <CIMSSelect
            simple
            style={{ minWidth: 250 }}
            disabled={!entityId}
            options={[...(CSVEntity.fromId(entityId)?.upsertKeys ?? [])].map(
              (upsertKey) => {
                return {
                  label: upsertKey,
                  value: upsertKey,
                }
              }
            )}
          />
        </CIMSFormItem>
      </div>
      {!!columns.length && (
        <Space
          direction="vertical"
          size="large"
          style={{ display: 'flex', marginBottom: '24px' }}
        >
          <CIMSSection title="Headers">
            <CIMSTable
              size="small"
              rowKey="column"
              records={rows}
              columns={[
                {
                  width: 100,
                  title: 'CSV Column',
                  render: ({ column }) => column,
                },
                {
                  width: 100,
                  title: 'Maps to field?',
                  render: ({ isValid, isUpsertKey }) =>
                    isValid ? (
                      <Space align={'center'} size={'small'}>
                        <CheckCircleTwoTone
                          twoToneColor="#52c41a"
                          style={{ fontSize: 20, display: 'flex' }}
                        />
                        <span>{isUpsertKey ? 'Upsert key' : ''}</span>
                      </Space>
                    ) : null,
                },
              ]}
            />
          </CIMSSection>
        </Space>
      )}
      <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
        {!!csvError && (
          <Alert
            message="CSV Error"
            description={csvError}
            type="error"
            showIcon
          />
        )}
        <Form.Item
          name="file"
          valuePropName="file"
          getValueFromEvent={(e) => e.file}
          rules={[
            { required: true, message: 'Please add a csv file to import' },
          ]}
        >
          <Dragger
            accept=".csv"
            beforeUpload={async (file) => {
              const isUnderLimit = file.size / 1024 / 1024 < 10
              if (!isUnderLimit) {
                setCSVError(`File size must be under 10MB`)
                return Upload.LIST_IGNORE
              }

              setCSVError(undefined)
              const text = await file.text()
              // Preferably we would stream this to avoid synchronously loading it
              // all in the browser's memory, but CSV Parse version 5.6.0 has
              // limited support for client-side streaming.
              let records: Record<string, unknown>[] | undefined
              setColumns([])
              try {
                records = parse(text, { columns: true, trim: true, bom: true })
              } catch (e) {
                if (e instanceof Error) {
                  setCSVError(e instanceof Error ? e.message : `Error: ${e}`)
                }
                return Upload.LIST_IGNORE
              }
              if (!records || !records.length) {
                setCSVError(
                  'The uploaded CSV file contains no rows. Please ensure the file is not empty and try again.'
                )
                return Upload.LIST_IGNORE
              }

              const upsertKey = form.getFieldValue('upsertKey')

              if (upsertKey && !(upsertKey in records[0])) {
                setCSVError(
                  `The selected upsert key "${upsertKey}" is not present in the uploaded CSV file's column headers. Please ensure the upsert key matches one of the column names in the file.`
                )
                return Upload.LIST_IGNORE
              }

              if (
                !Object.keys(records[0]).some(
                  (column) =>
                    column !== upsertKey && allowedColumns.includes(column)
                )
              ) {
                setCSVError(
                  `No valid columns found in the uploaded CSV file. Ensure that at least one column (other than the upsert key) matches the expected column names: ${[
                    ...allowedColumns,
                  ].join(', ')}.`
                )
                return Upload.LIST_IGNORE
              }

              if (Object.keys(records[0])) {
                setColumns(records.length ? Object.keys(records[0]) : [])
              }

              return false
            }}
            onRemove={() => {
              setCSVError(undefined)
              setColumns([])
            }}
            style={{ width: '100%' }}
            maxCount={1}
            multiple
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Click or drag file to this area to upload
            </p>
          </Dragger>
        </Form.Item>
      </Space>
    </CIMSModal>
  )
}

export default ImportModal
